<script>
  import 'mapbox-gl/dist/mapbox-gl.css';
  import mapboxgl from 'mapbox-gl';
  import { onMount, onDestroy, tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  import { getGroupStore } from '@stores/group';
  let recObjectsStore = getGroupStore('recObjectsStore');
  import ComponentEvents from '@utils/component-events';

  import DetailViewModal from '@app-components/rec_object/detail_view/DetailViewModal.svelte';
  import MapDetailCard from '@app-components/group/MapDetailCard.svelte';

  import { getMapViewport, getGeodata, defaultZoom, defaultCenter } from '@app-components-helper/group/map';

  import jQuery from 'jquery';
  import queryString from 'query-string';

  mapboxgl.accessToken = import.meta.env['VITE_MAPBOX_API_TOKEN'];

  export let customClickHandler;
  export let setViewportAutomatically = true;

  let detailCardPosition = {};
  let recObjects = [];

  $: recObjectsWithLocation = recObjects.filter((recObject) => getGeodata(recObject).location);

  $: noMapItems = !(recObjectsWithLocation && recObjectsWithLocation.length);

  let map;
  let recObjectIdsToMarkerEls = {};
  let markerToRecObjectMapping = {};
  let hoveredMarkerId;
  let clickedMarkerId;
  let mapViewport = {};

  // detail modal variables
  let showDetailViewModal = false;
  let detailModalRecObjectId;

  // detail card (map popup) variables
  let detailCardRecObjectId;

  const openDetailViewModal = (recObjectId) => {
    detailModalRecObjectId = recObjectId;
    showDetailViewModal = true;
  };

  const determinePopupAnchorAndOffset = (markerId) => {
    const markerSelector = `#${markerId}`;

    const diagonalOffset = 14;
    const straightOffset = 26;
    const preferences = [
      { anchor: 'bottom', offset: straightOffset },
      { anchor: 'bottom-left', offset: diagonalOffset },
      { anchor: 'bottom-right', offset: diagonalOffset },
      { anchor: 'left', offset: straightOffset },
      { anchor: 'right', offset: straightOffset },
      { anchor: 'top', offset: straightOffset },
      { anchor: 'top-left', offset: diagonalOffset },
      { anchor: 'top-right', offset: diagonalOffset },
    ];

    const popupWidth = 340;
    const popupHeight = 104;
    const mapHeight = jQuery('.mapboxgl-canvas').height();
    const mapWidth = jQuery('.mapboxgl-canvas').width();

    const { top: markerTop, left: markerLeft } = jQuery(markerSelector).position();

    const markerCenterX = markerLeft + jQuery(markerSelector).width() / 2;
    // NOTE: marker center Y is very slightly different than the location of the exact latitude
    // (some kind of vertical centering of the marker image). This makes us off by about 16px,
    // in the "good" buffer direction; so we left the math simpler here despite the tiny error.
    const markerCenterY = markerTop + jQuery(markerSelector).height() / 2;

    let desiredAnchorAndPosition;
    let anchorWithMinimumError = {
      error: 1000,
    };

    const checkBounds = ({ x, y }) => {
      const xOverlapLeft = 0 - x;
      const xOverlapRight = x - mapWidth;
      const yOverlapTop = 0 - y;
      const yOverlapBottom = y - mapHeight;

      const isWithinBounds = xOverlapLeft <= 0 && xOverlapRight <= 0 && yOverlapTop <= 0 && yOverlapBottom <= 0;

      const worstError = Math.max(xOverlapLeft, xOverlapRight, yOverlapTop, yOverlapBottom);

      return { isWithinBounds, worstError };
    };

    preferences.forEach(({ anchor, offset }) => {
      if (desiredAnchorAndPosition) {
        return;
      }

      let popupTop;
      let popupLeft;

      switch (anchor) {
        case 'bottom':
          popupTop = markerCenterY - offset - popupHeight;
          popupLeft = markerCenterX - popupWidth / 2;
          break;
        case 'bottom-left':
          popupTop = markerCenterY - offset - popupHeight;
          popupLeft = markerCenterX + offset;
          break;
        case 'bottom-right':
          popupTop = markerCenterY - offset - popupHeight;
          popupLeft = markerCenterX - offset - popupWidth;
          break;
        case 'right':
          popupTop = markerCenterY - popupHeight / 2;
          popupLeft = markerCenterX - offset - popupWidth;
          break;
        case 'left':
          popupTop = markerCenterY - popupHeight / 2;
          popupLeft = markerCenterX + offset;
          break;
        case 'top':
          popupTop = markerCenterY + offset;
          popupLeft = markerCenterX - popupWidth / 2;
          break;
        case 'top-right':
          popupTop = markerCenterY + offset;
          popupLeft = markerCenterX - offset - popupWidth;
          break;
        case 'top-left':
          popupTop = markerCenterY + offset;
          popupLeft = markerCenterX + offset;
          break;
      }

      const popupBottom = popupTop + popupHeight;
      const popupRight = popupLeft + popupWidth;

      const { isWithinBounds: topLeftWithinBounds, worstError: topLeftWorstError } = checkBounds({
        x: popupLeft,
        y: popupTop,
      });

      const { isWithinBounds: bottomRightWithinBounds, worstError: bottomRightWorstError } = checkBounds({
        x: popupRight,
        y: popupBottom,
      });

      const worstError = Math.max(topLeftWorstError, bottomRightWorstError);

      if (worstError < anchorWithMinimumError.error) {
        anchorWithMinimumError = {
          error: worstError,
          anchor: anchor,
          top: popupTop,
          left: popupLeft,
        };
      }

      if (topLeftWithinBounds && bottomRightWithinBounds) {
        desiredAnchorAndPosition = {
          anchor,
          top: popupTop,
          left: popupLeft,
        };
      }
    });

    // If no positioning fits inside the bounds, choose the location that
    // overruns the map the least.
    if (!desiredAnchorAndPosition) {
      desiredAnchorAndPosition = anchorWithMinimumError;
    }

    return desiredAnchorAndPosition;
  };

  const showDetailCard = (elementId) => {
    const recObjectId = markerToRecObjectMapping[elementId];
    detailCardRecObjectId = recObjectId;

    // update detail card position
    const { top: relativeTop, left: relativeLeft } = determinePopupAnchorAndOffset(elementId);
    const { top: mapTop, left: mapLeft } = jQuery('.mapboxgl-canvas').offset();
    const { scrollY } = window;

    detailCardPosition = {
      top: `${relativeTop + mapTop - scrollY}px`,
      left: `${relativeLeft + mapLeft}px`,
      bottom: 'unset',
    };
  };

  const removeDetailCard = () => {
    detailCardRecObjectId = undefined;
  };

  const markerClicked = (elementOrId, openModal = false) => {
    let element = elementOrId;

    if (typeof elementOrId === 'string') {
      element = document.querySelector(`#${elementOrId}`);
    }

    if (customClickHandler) {
      return customClickHandler(element.id.match(/\d/g).join(''));
    }

    if (clickedMarkerId) {
      setDefaultMarkerValues(clickedMarkerId);
    }

    setClickedMarkerValues(element);
    clickedMarkerId = element.id;

    if (openModal) {
      const recObjectId = markerToRecObjectMapping[element.id];
      openDetailViewModal(recObjectId);
    }

    removeDetailCard();
  };

  const markerHovered = (elementOrId) => {
    let element = elementOrId;
    if (typeof elementOrId === 'string') {
      element = document.querySelector(`#${elementOrId}`);
    }

    hoveredMarkerId = element.id;
    setHoveredMarkerValues(element);
    showDetailCard(hoveredMarkerId);
  };

  const markerUnhovered = (elementOrId) => {
    let element = elementOrId;
    if (typeof elementOrId === 'string') {
      element = document.querySelector(`#${elementOrId}`);
    }

    if (clickedMarkerId === element.id) {
      setClickedMarkerValues(element);
    } else {
      markerToDefault(element);
    }
    removeDetailCard();
  };

  const markerToDefault = (elementOrId) => {
    let element = elementOrId;
    if (typeof elementOrId === 'string') {
      element = document.querySelector(`#${elementOrId}`);
    }

    setDefaultMarkerValues(element);
    hoveredMarkerId = undefined;
  };

  const initMap = () => {
    // only init map once
    if (map) {
      return;
    }

    // find viewport options
    let mapViewportOptions = {
      center: [defaultCenter.lng, defaultCenter.lat],
      zoom: defaultZoom,
    };

    const { southwest, northeast } = getMapViewport(recObjectsWithLocation);
    if (southwest) {
      mapViewportOptions = { bounds: [southwest, northeast] };
    }

    // combine with default options and init map.
    map = new mapboxgl.Map({
      ...{
        container: 'map-view__map', // container ID
        style: 'mapbox://styles/mapbox/light-v10', // style URL
      },
      ...mapViewportOptions,
    });

    // disable map rotation using right click + drag
    map.dragRotate.disable();
    // disable map rotation using touch rotation gesture
    map.touchZoomRotate.disableRotation();
    // disable map pitch adjustment with two finger slide
    // (may not matter on desktop but doesn't hurt to disable)
    map.touchPitch.disable();

    // Unhighlight any markers when the map is clicked outside a marker.
    map.on('click', (e) => {
      const { clientX, clientY } = e.originalEvent;
      const intersectedMarkers = document
        .elementsFromPoint(clientX, clientY)
        .filter((el) => [...el.classList].includes('marker'));

      if (intersectedMarkers.length) {
        return;
      }

      if (clickedMarkerId) {
        markerToDefault(clickedMarkerId);
        removeDetailCard();
        clickedMarkerId = undefined;
      }
    });

    dispatch('init-map', map);
  };

  const setMapViewportBasedOnRecObjects = () => {
    // update component variable
    mapViewport = getMapViewport(recObjectsWithLocation);

    // set on map if map exists
    const { southwest, northeast } = mapViewport;

    if (map && southwest && northeast) {
      map.fitBounds([southwest, northeast]);
    }
  };

  const setDefaultMarkerValues = (elementOrId) => {
    let el = elementOrId;
    if (typeof elementOrId === 'string') {
      el = document.querySelector(`#${elementOrId}`);
    }

    el.classList.remove('marker-clicked', 'marker-hovered');
    el.classList.add('marker-default');
    el.src = '/images/map/marker.svg';
    el.style.width = '27px';
    el.style.height = '41px';
  };

  const setHoveredMarkerValues = (elementOrId) => {
    let el = elementOrId;
    if (typeof elementOrId === 'string') {
      el = document.querySelector(`#${elementOrId}`);
    }

    el.classList.remove('marker-default', 'marker-clicked');
    el.classList.add('marker-hovered');
    el.src = '/images/map/marker-black.svg';
    el.style.width = '30px';
    el.style.height = '45px';
  };

  const setClickedMarkerValues = (elementOrId) => {
    let el = elementOrId;
    if (typeof elementOrId === 'string') {
      el = document.querySelector(`#${elementOrId}`);
    }

    el.classList.remove('marker-default', 'marker-hovered');
    el.classList.add('marker-clicked');
    el.src = '/images/map/marker-black.svg';
    el.style.width = '27px';
    el.style.height = '41px';
  };

  const redrawMarkers = () => {
    hoveredMarkerId = undefined;
    removeDetailCard();

    // draw new markers:
    recObjectsWithLocation.forEach((recObject) => {
      if (document.getElementById(`map-marker-for-${recObject.id}`)) {
        return;
      }

      const { location } = getGeodata(recObject);

      const el = document.createElement('img');
      el.id = `map-marker-for-${recObject.id}`;
      el.alt = '[map marker]';
      el.classList.add('marker');
      setDefaultMarkerValues(el);

      el.addEventListener('mouseover', (e) => {
        markerHovered(e.currentTarget);
      });

      el.addEventListener('mouseout', (e) => {
        markerUnhovered(e.currentTarget);
      });

      el.addEventListener('click', (e) => {
        markerClicked(e.currentTarget, true);
      });

      markerToRecObjectMapping[el.id] = recObject.id;
      recObjectIdsToMarkerEls[recObject.id] = el;

      new mapboxgl.Marker({ element: el }).setLngLat([location.lng, location.lat]).addTo(map);
    });

    // if the previously "clicked" marker no longer exists, make sure it isn't set
    // as the "clicked" marker in memory.
    if (clickedMarkerId && !document.querySelector(`#${clickedMarkerId}`)) {
      clickedMarkerId = undefined;
    }
  };

  const unsubRecObjects = recObjectsStore.subscribe(async (newRecObjects) => {
    recObjects = newRecObjects;

    if (map) {
      await tick();
      redrawMarkers();

      if (setViewportAutomatically) {
        setMapViewportBasedOnRecObjects();
      }

      if (clickedMarkerId) {
        markerClicked(clickedMarkerId);
      }
    }
  });

  const unsubHoverRecObject = ComponentEvents.on('rec-object-hover', (data) => {
    const markerElement = recObjectIdsToMarkerEls[data.recObject.id];

    // this can happen if recObject doesn't have a location
    if (!markerElement) {
      return;
    }
    markerHovered(markerElement);
  });

  const unsubUnhoverRecObject = ComponentEvents.on('rec-object-unhover', (data) => {
    const markerElement = recObjectIdsToMarkerEls[data.recObject.id];

    // this can happen if recObject doesn't have a location
    if (!markerElement) {
      return;
    }
    markerUnhovered(markerElement);
  });

  const unsubClickedRecObject = ComponentEvents.on('rec-object-click', (data) => {
    const markerElement = recObjectIdsToMarkerEls[data.recObject.id];

    // this can happen if recObject doesn't have a location
    if (!markerElement) {
      return;
    }
    markerClicked(markerElement);
  });

  const popStateListener = function (_event) {
    let detailId = queryString.parse(location.search).detail_id;
    showDetailViewModal = detailId && detailModalRecObjectId == detailId;
  };

  onMount(() => {
    initMap();
    redrawMarkers();

    window.addEventListener('popstate', popStateListener);
  });

  onDestroy(() => {
    unsubRecObjects();
    unsubHoverRecObject();
    unsubUnhoverRecObject();
    unsubClickedRecObject();

    window.removeEventListener('popstate', popStateListener);
  });
</script>

<!-- NOTE: this is global so map can pick it up, so ensure that it's all scoped within the #map-view__map selector -->
<style style="text/scss" lang="scss" global>
  #map-view__map {
    border-radius: 10px;
    width: 100%;
    height: 100%;

    .mapbox-improve-map {
      display: none;
    }

    &.no-map-items {
      &:after {
        content: ' ';
        z-index: 3;
        display: block;
        position: absolute;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.5);
      }

      .map-view__no-items-text {
        position: absolute;
        z-index: 4;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .marker {
      cursor: pointer;
    }

    .map-view__map-popup {
      cursor: pointer;
      border-radius: 15px;
      box-shadow: 0px 13px 40px rgb(0 0 0 / 9%);

      &:hover {
        box-shadow: 0px 13px 40px rgb(0 0 0 / 18%);
      }

      .mapboxgl-popup-tip {
        display: none;
      }

      .mapboxgl-popup-close-button {
        display: none;
      }

      .mapboxgl-popup-content {
        width: 240px;
        border-radius: 8px;
        padding: 8px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;

        .map-tooltip-title {
          font-weight: 500;
          font-size: 13px;
          line-height: 16px;
          padding: 5px 10px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          word-break: break-word;
          flex-grow: 1;
        }

        img {
          object-fit: cover;
          flex-shrink: 0;
          width: 80px;
          height: 64px;
          border-radius: 6px;
        }
      }
    }
  }
</style>

<div class="map-view__map" class:no-map-items={noMapItems} id="map-view__map">
  {#if noMapItems}
    <div class="map-view__no-items-text">
      {'Add locations to any recommendations to make them appear on the map'}
    </div>
  {/if}

  {#if detailCardRecObjectId}
    <MapDetailCard
      withFooter={false}
      recObjectId={detailCardRecObjectId}
      recObjectsWithLocation={recObjectsWithLocation}
      mobile={false}
      positionStyle={detailCardPosition} />
  {/if}

  {#if detailModalRecObjectId && showDetailViewModal}
    <DetailViewModal mobile={false} recObjectId={detailModalRecObjectId} bind:showModal={showDetailViewModal} />
  {/if}
</div>
